<script setup lang="ts">
import { useSegmentAdapter } from '@/shared/adapters/matching/useSegmentAdapter'
import DeleteEmployeeAccountDialog from '@/features/tools/dialogs/DeleteEmployeeAccountDialog.vue'
import { usePermissions } from '@/shared/composables/UsePermissions'
import { useToast } from '@/shared/composables/UseToast'
import {
  deleteAdminOnboardingUser,
  getListAdminMatchingCandidatesQueryKey,
  useAdminListReferralSources,
  useListAdminMatchingCandidates,
  useListAdminMatchingJobCategories,
  useListAdminMatchingJobFunctions,
  useListEducationLevels,
  useListSegments,
} from '@swipe4work/api-orval'
import {
  AdminPermissionResource,
  AgeBucket,
  ExperienceLevel,
  JobSeekingStatus,
  LastOnlineFilter,
  type ListAdminMatchingCandidatesCandidateListItemDto,
  PermissionLevel,
  Province,
  SegmentType,
} from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'
import { useConfirm } from 'primevue/useconfirm'
import { useRouter } from 'vue-router'

definePage({
  path: '/matching/candidates',
  name: '/matching/Candidates',
  meta: {
    title: 'Kandidaten',
    permission: {
      resource: 'Candidates',
      level: 'Read',
    },
  },
})

const router = useRouter()

const { jobSeekingStatusOptions } = useSegmentAdapter()

const provinceOptions = Object.values(Province).map(p => ({
  label: p,
  value: p, 
}))

const PAGE_SIZE = 10

type FilterState = {
  jobCategoryIds: number[]
  jobFunctionIds: number[]
  experienceLevels: ExperienceLevel[]
  educationLevelIds: number[]
  provinces: Province[]
  ageBuckets: AgeBucket[]
  jobSeekingStatuses: JobSeekingStatus[]
  lastOnlineFilter: LastOnlineFilter | null
  referralSourceIds: number[]
  profileCompletionMin: number | null
  profileCompletionMax: number | null
  segmentId: number | null
  isIdeal: boolean | null
  isOnboardingUser: boolean | null
}

const {
  filters,
  draft,
  searchInput,
  search,
  sortField,
  sortDescending,
  offset,
  activeFilterCount,
  onFilterPanelShow,
  onFilterApply,
  onFilterClear,
  onSort,
  onPage,
} = useDataTableFilters<FilterState>({
  schema: {
    jobCategoryIds: 'ids',
    jobFunctionIds: 'ids',
    experienceLevels: 'enums',
    educationLevelIds: 'ids',
    provinces: 'enums',
    ageBuckets: 'enums',
    jobSeekingStatuses: 'enums',
    lastOnlineFilter: 'enum',
    referralSourceIds: 'ids',
    profileCompletionMin: 'number',
    profileCompletionMax: 'number',
    segmentId: 'id',
    isIdeal: 'boolean',
    isOnboardingUser: 'boolean',
  },
  defaultFilters: () => ({
    jobCategoryIds: [],
    jobFunctionIds: [],
    experienceLevels: [],
    educationLevelIds: [],
    provinces: [],
    ageBuckets: [],
    jobSeekingStatuses: [],
    lastOnlineFilter: null,
    referralSourceIds: [],
    profileCompletionMin: null,
    profileCompletionMax: null,
    segmentId: null,
    isIdeal: null,
    isOnboardingUser: null,
  }),
  defaultPageSize: PAGE_SIZE,
  persistToStorage: true,
})

// Reference data (cached forever)
const { data: jobCategoriesData } = useListAdminMatchingJobCategories({ query: { staleTime: Infinity } })
const { data: jobFunctionsData } = useListAdminMatchingJobFunctions({ query: { staleTime: Infinity } })
const { data: educationLevelsData } = useListEducationLevels({ query: { staleTime: Infinity } })
const { data: referralSourcesData } = useAdminListReferralSources({ query: { staleTime: Infinity } })
const { data: segmentsData } = useListSegments(
  { type: SegmentType.User },
  { query: { staleTime: 5 * 60 * 1000 } },
)

const jobCategoryOptions = computed(() => jobCategoriesData.value?.jobCategories ?? [])
const jobFunctionOptions = computed(() => jobFunctionsData.value?.jobFunctions ?? [])
const educationLevelOptions = computed(() => educationLevelsData.value?.educationLevels ?? [])
const referralSourceOptions = computed(() => referralSourcesData.value?.referralSources ?? [])
const segmentOptions = computed(() => segmentsData.value?.segments ?? [])

const jobCategoryNameById = computed(() =>
  Object.fromEntries(jobCategoryOptions.value.map(c => [ c.id, c.name ])),
)

const functionIdsByCategoryId = computed(() => {
  const map = new Map<number, Set<number>>()

  for (const fn of jobFunctionOptions.value) {
    for (const categoryId of fn.jobCategoryIds) {
      let set = map.get(categoryId)
      if (!set) {
        set = new Set<number>()
        map.set(categoryId, set)
      }
      set.add(fn.id)
    }
  }

  return map
})

function getCategoriesWithFunctions(candidate: ListAdminMatchingCandidatesCandidateListItemDto) {
  return candidate.jobCategories.map(cat => {
    const fnIdsInCategory = functionIdsByCategoryId.value.get(cat.id) ?? new Set<number>()

    return {
      id: cat.id,
      name: cat.name,
      functions: candidate.jobFunctions.filter(f => fnIdsInCategory.has(f.id)),
    }
  })
}

// Candidates query — driven by committed filters only
const params = computed(() => ({
  search: search.value,
  jobCategoryIds: filters.jobCategoryIds.length > 0 ? filters.jobCategoryIds : undefined,
  jobFunctionIds: filters.jobFunctionIds.length > 0 ? filters.jobFunctionIds : undefined,
  experienceLevels: filters.experienceLevels.length > 0 ? filters.experienceLevels : undefined,
  educationLevelIds: filters.educationLevelIds.length > 0 ? filters.educationLevelIds : undefined,
  provinces: filters.provinces.length > 0 ? filters.provinces : undefined,
  ageBuckets: filters.ageBuckets.length > 0 ? filters.ageBuckets : undefined,
  jobSeekingStatuses: filters.jobSeekingStatuses.length > 0 ? filters.jobSeekingStatuses : undefined,
  lastOnlineFilter: filters.lastOnlineFilter ?? undefined,
  referralSourceIds: filters.referralSourceIds.length > 0 ? filters.referralSourceIds : undefined,
  profileCompletionMin: filters.profileCompletionMin ?? undefined,
  profileCompletionMax: filters.profileCompletionMax ?? undefined,
  segmentId: filters.segmentId ?? undefined,
  isIdeal: filters.isIdeal ?? undefined,
  isOnboardingUser: filters.isOnboardingUser ?? undefined,
  sortField: sortField.value,
  sortDescending: sortDescending.value ? true : undefined,
  Limit: PAGE_SIZE,
  Offset: offset.value,
}))

const { data, isPending, isError } = useListAdminMatchingCandidates(params)

const candidates = computed(() => data.value?.candidates ?? [])
const totalCount = computed(() => data.value?.totalCount ?? 0)

function goToCandidate(candidate: ListAdminMatchingCandidatesCandidateListItemDto) {
  router.push({
    name: '/matching/CandidateDetail',
    params: { userId: candidate.userId },
  })
}

const menu = ref()
const selectedCandidate = ref<ListAdminMatchingCandidatesCandidateListItemDto | null>(null)
const dialog = useDynamicDialog()

const confirm = useConfirm()
const toast = useToast()
const queryClient = useQueryClient()

const { can } = usePermissions()
const canCandidatesDelete = computed(() => can(AdminPermissionResource.Candidates, PermissionLevel.Delete))

function confirmDeleteOnboardingUser(candidate: ListAdminMatchingCandidatesCandidateListItemDto) {
  confirm.require({
    header: 'Onboarding gebruiker verwijderen',
    message: `Weet je zeker dat je ${candidate.displayName || candidate.email || 'deze onboarding gebruiker'} wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`,
    acceptProps: {
      label: 'Verwijderen',
      severity: 'danger',
    },
    rejectProps: {
      label: 'Annuleren',
      severity: 'secondary',
    },
    accept: async () => {
      try {
        await deleteAdminOnboardingUser(candidate.userId)
        await queryClient.invalidateQueries({ queryKey: getListAdminMatchingCandidatesQueryKey() })
        toast.success('Verwijderd', 'Onboarding gebruiker is verwijderd')
      }
      catch {
        toast.error('Fout', 'Onboarding gebruiker kon niet worden verwijderd')
      }
    },
  })
}

const menuItems = computed(() => {
  const candidate = selectedCandidate.value
  if (!candidate) {
    return []
  }

  if (candidate.isOnboardingUser) {
    return [
      {
        label: 'Verwijderen',
        icon: canCandidatesDelete.value ? 'fa-regular fa-user-slash' : 'fa-regular fa-lock',
        disabled: !canCandidatesDelete.value,
        command: () => confirmDeleteOnboardingUser(candidate),
      },
    ]
  }

  return [
    {
      label: 'Bekijk',
      icon: 'fa-regular fa-info-circle',
      command: () => goToCandidate(candidate),
    },
    { separator: true },
    {
      label: 'Verwijderen',
      icon: 'fa-regular fa-user-slash',
      command: () => dialog.open(DeleteEmployeeAccountDialog),
    },
  ]
})

function handleClickRow(event: any, candidate: ListAdminMatchingCandidatesCandidateListItemDto) {
  selectedCandidate.value = candidate
  menu.value.show(event.originalEvent)
}
</script>

<template>
  <div class="space-y-5">
    <ContextMenu
      ref="menu"
      :model="menuItems"
      @hide="selectedCandidate = null"
    />
  
    <Card>
      <template #content>
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between gap-4">
            <h3 class="text-lg font-semibold">
              Kandidaten
            </h3>
            <div class="flex items-center gap-3">
              <DataTableFilterPanel
                :active-filter-count="activeFilterCount"
                @show="onFilterPanelShow"
                @apply="onFilterApply"
                @clear="onFilterClear"
              >
                <CandidateFilters
                  :draft="draft"
                  :segment-options="segmentOptions"
                  :job-category-options="jobCategoryOptions"
                  :job-function-options="jobFunctionOptions"
                  :job-category-name-by-id="jobCategoryNameById"
                  :education-level-options="educationLevelOptions"
                  :province-options="provinceOptions"
                  :job-seeking-status-options="jobSeekingStatusOptions"
                  :referral-source-options="referralSourceOptions"
                />
              </DataTableFilterPanel>

              <IconField class="max-w-xs flex-1">
                <InputIcon class="fa-regular fa-search" />
                <InputText
                  v-model="searchInput"
                  placeholder="Zoeken op naam, e-mail of stad..."
                  class="w-full"
                />
                <InputIcon
                  v-if="searchInput"
                  class="fa-regular fa-xmark cursor-pointer"
                  style="pointer-events: auto"
                  @click="searchInput = ''"
                />
              </IconField>
            </div>
          </div>

          <div v-if="isPending" class="flex flex-col gap-2">
            <Skeleton v-for="n in 8" :key="n" height="3rem" />
          </div>
          <Message v-else-if="isError" severity="error">
            Er is iets misgegaan bij het laden van kandidaten.
          </Message>
          <div v-else class="overflow-x-auto">
            <DataTable
              :value="candidates"
              :lazy="true"
              :first="offset"
              :rows="PAGE_SIZE"
              :total-records="totalCount"
              :sort-field="sortField"
              :sort-order="sortDescending ? -1 : 1"
              paginator
              size="small"
              table-style="table-layout: fixed"
              class="w-full"
              row-hover
              @row-click="(e) => handleClickRow(e, e.data)"
              @page="onPage"
              @sort="onSort"
            >
              <Column field="displayName" header="Naam" sortable style="width: 12rem" header-class="text-xs">
                <template #body="{ data: candidate }">
                  <div class="flex flex-col">
                    <span class="flex items-center gap-1 text-sm font-medium">
                      <span class="truncate" :title="candidate.displayName">{{ candidate.displayName || '—' }}</span>
                      <span
                        v-if="candidate.isOnboardingUser"
                        v-tooltip.top="'Heeft de onboarding nog niet afgerond'"
                        class="shrink-0 rounded-full bg-yellow-50 px-1.5 text-[10px] text-yellow-700 font-medium leading-3.5 ring-1 ring-yellow-400"
                      >
                        Onboarding
                      </span>
                      <i
                        v-if="candidate.isIdeal"
                        v-tooltip.top="'Ideal User'"
                        class="fa-solid fa-star shrink-0 text-xs text-primary"
                      />
                    </span>
                    <span class="block truncate text-xs text-surface-400" :title="candidate.email">{{ candidate.email }}</span>
                    <span v-if="candidate.referralSources.length > 0" class="block truncate text-xs text-surface-400" :title="candidate.referralSources.join(', ')">{{ candidate.referralSources.join(', ') }}</span>
                  </div>
                </template>
              </Column>
              <Column field="lastLoginAt" header="Laatst online" sortable style="width: 8rem" header-class="text-xs">
                <template #body="{ data: candidate }">
                  <div class="flex flex-col">
                    <span class="text-sm">{{ candidate.lastLoginAt ? useFormatDate(candidate.lastLoginAt, DateFormat.Date) : '—' }}</span>
                    <span class="text-xs text-surface-400">{{ useFormatDate(candidate.createdAt, DateFormat.Date) }}</span>
                  </div>
                </template>
              </Column>
              <Column header="Locatie" style="width: 9rem" header-class="text-xs">
                <template #body="{ data: candidate }">
                  <div class="flex flex-col">
                    <span class="block truncate text-sm">{{ candidate.searchProfileCity || '—' }}</span>
                    <span v-if="candidate.searchProfileProvince" class="block truncate text-xs text-surface-400">{{ candidate.searchProfileProvince }}</span>
                  </div>
                </template>
              </Column>
              <Column header="Beroepsgroepen / Functies" style="width: 14rem" header-class="text-xs">
                <template #body="{ data: candidate }">
                  <JobCategoriesWithFunctionsList
                    :categories="getCategoriesWithFunctions(candidate)"
                    dense
                    empty-label="—"
                  />
                </template>
              </Column>
              <Column field="experienceLevel" header="Ervaring / Opleiding" sortable style="width: 9rem" header-class="text-xs">
                <template #body="{ data: candidate }">
                  <div class="flex flex-col">
                    <span class="text-sm">{{ candidate.experienceLevel ?? '—' }}</span>
                    <span v-if="candidate.educationLevelName" class="block truncate text-xs text-surface-400">{{ candidate.educationLevelName }}</span>
                  </div>
                </template>
              </Column>
              <Column header="Status" style="width: 8rem" header-class="text-xs">
                <template #body="{ data: candidate }">
                  <PillTag
                    v-if="candidate.jobSeekingStatus"
                    :value="candidate.jobSeekingStatus === 'ActivelyLooking' ? 'Actief zoekend' : 'Open voor kansen'"
                    :severity="candidate.jobSeekingStatus === 'ActivelyLooking' ? 'success' : 'info'"
                  />
                  <span v-else class="text-sm text-surface-400">—</span>
                </template>
              </Column>
              <Column field="totalMatchScoreCount" header="Match scores" sortable style="width: 7rem" header-class="text-xs">
                <template #body="{ data: candidate }">
                  <span v-if="candidate.isOnboardingUser" class="text-sm text-surface-400">—</span>
                  <div v-else class="flex flex-col">
                    <span class="text-sm font-semibold">{{ candidate.totalMatchScoreCount }}</span>
                    <span class="text-xs text-surface-400">{{ candidate.visibleMatchScoreCount }} zichtbaar</span>
                  </div>
                </template>
              </Column>
              <Column field="profileCompletionScore" header="Profiel" sortable style="width: 5rem" header-class="text-xs text-right" class="text-right">
                <template #body="{ data: candidate }">
                  <span v-if="candidate.profileCompletionScore == null" class="text-sm text-surface-400">—</span>
                  <span v-else class="text-sm font-semibold">{{ candidate.profileCompletionScore }}%</span>
                </template>
              </Column>
              <Column style="width: 3rem">
                <template #body="{ data: candidate }">
                  <i v-if="!candidate.isOnboardingUser" class="fa-regular fa-chevron-right text-surface-400" />
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>
